/**
 * DriversScreen - Fleet Overview with Map and Dispatch List
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - Solution 1: Separate map loading from list rendering
 *   The dispatch list renders immediately using cached markers while geocoding
 *   happens asynchronously in the background. This eliminates the loading delay.
 *
 * - Solution 3: Use coordinates from sourceMetadata
 *   If dispatch records contain pickupLocationLat/Long in sourceMetadata (from Urgently
 *   or other sources), we use them directly instead of calling Google Geocoding API.
 *   Only missing coordinates are geocoded, reducing API calls and latency.
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLoadScript, GoogleMap, Marker } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Header from '@/components/common/Header';
import { ActiveServiceCallsTable } from '@/components/ActiveServiceCallsTable';
import { useCompanyDrivers, CompanyDriver } from '@/hooks/useCompanyDrivers';
import { useCompanyDispatches } from '@/hooks/useCompanyDispatches';
import { useGetCustomers } from '@/api/hooks/useGetCustomers';
import { useDriverStateV2 } from '@/hooks/useDriverStateV2';
import { useCompanyInfo } from '@/api/hooks/useCompanyInfo';
import CreateCallScreen from '@/screens/calls/CreateCallScreen';
import CreateCallBar from '@/components/common/CreateCallBar';
import { Loader2, AlertCircle, Map, Eye } from 'lucide-react';

interface DriversScreenProps {
  showHeader?: boolean;
  onViewAllDispatch?: () => void;
  activeTab?: string;
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
// Static array to prevent unnecessary LoadScript reloads
const GOOGLE_MAPS_LIBS: 'places'[] = ['places'];

const mapContainerStyle = {
  width: '100%',
  height: '400px',
};

const DEFAULT_FALLBACK_CENTER = {
  lat: 32.8328,
  lng: -117.1712,
};

// Function to get status color based on dispatch status - matches CallScreen.tsx
const getStatusColor = (status: string) => {
  // Convert backend status to frontend status (matching CallScreen.tsx)
  const statusMap: { [key: string]: string } = {
    NEW: 'ready',
    PENDING: 'waiting',
    DISPATCHED: 'en_route',
    ON_SCENE: 'on_scene',
    TOWING: 'towing',
    COMPLETED: 'complete',
    FAILED: 'complete',
  };

  const mappedStatus = statusMap[status] || status;

  // Return hex color pairs that match Tailwind classes used in CallScreen
  switch (mappedStatus) {
    case 'ready': // NEW - green-600
      return { bg: '#dcfce7', text: '#166534' }; // light green background
    case 'waiting': // PENDING - yellow-600
      return { bg: '#fef3c7', text: '#92400e' }; // light yellow background
    case 'en_route': // DISPATCHED - blue-600
      return { bg: '#dbeafe', text: '#0369a1' }; // light blue background
    case 'on_scene': // ON_SCENE - orange-600
      return { bg: '#fed7aa', text: '#92400b' }; // light orange background
    case 'towing': // TOWING - teal-600
      return { bg: '#ccfbf1', text: '#0d9488' }; // light teal background
    case 'complete': // COMPLETED or FAILED - purple-600
      return { bg: '#e9d5ff', text: '#6b21a8' }; // light purple background
    default:
      return { bg: '#f3f4f6', text: '#4b5563' }; // light gray background
  }
};

// Function to get status color based on driver status (ACTIVE, CLEAR, DISPATCHED, ON_LEAVE)
const getDriverStatusColor = (status: string) => {
  switch (status) {
    case 'WAITING': // Waiting - light grey
      return { bg: '#f3f4f6', text: '#6b7280' }; // light gray background
    case 'DISPATCHED': // Dispatched - green
      return { bg: '#dcfce7', text: '#166534' }; // light green background
    case 'EN_ROUTE': // En Route - blue
      return { bg: '#dbeafe', text: '#0369a1' }; // light blue background
    case 'ON_SCENE': // On Scene - orange
      return { bg: '#fed7aa', text: '#92400b' }; // light orange background
    case 'TOWING': // Towing - turquoise
      return { bg: '#ccfbf1', text: '#0d9488' }; // light turquoise background
    case 'RETURNING': // Returning - purple
      return { bg: '#e9d5ff', text: '#6b21a8' }; // light purple background
    case 'OFFLINE': // Offline - dark gray
      return { bg: '#e5e7eb', text: '#4b5563' }; // light gray background
    case 'ACTIVE': // Legacy - green
      return { bg: '#dcfce7', text: '#166534' }; // light green background
    case 'CLEAR': // Legacy - gray
      return { bg: '#f3f4f6', text: '#6b7280' }; // light gray background
    case 'ON_LEAVE': // Legacy - gray
      return { bg: '#f3f4f6', text: '#6b7280' }; // light gray background
    default:
      return { bg: '#f3f4f6', text: '#4b5563' }; // light gray background
  }
};

// Function to create custom marker icon with CarFront icon and colored circle background
const createCustomMarkerIcon = (status: string) => {
  const colors = getStatusColor(status);

  // Create a canvas to render the icon
  const canvas = document.createElement('canvas');
  canvas.width = 36;
  canvas.height = 36;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    // Draw circle background with shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 3;
    ctx.shadowOffsetY = 2;
    ctx.fillStyle = colors.bg;
    ctx.beginPath();
    ctx.arc(18, 18, 16.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowColor = 'transparent';

    // Draw the CarFront icon using canvas paths
    ctx.strokeStyle = colors.text;
    ctx.fillStyle = colors.text;
    ctx.lineWidth = 1.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Translate to center
    ctx.translate(18, 18);

    // Draw windshield/hood
    ctx.beginPath();
    ctx.moveTo(-5, -4);
    ctx.lineTo(5, -4);
    ctx.lineTo(6, -1);
    ctx.lineTo(-6, -1);
    ctx.closePath();
    ctx.stroke();

    // Draw body
    ctx.fillRect(-6, -1, 12, 6);

    // Draw wheels
    ctx.beginPath();
    ctx.arc(-3, 5, 1.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.arc(3, 5, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }

  return {
    url: canvas.toDataURL(),
    scaledSize: new google.maps.Size(36, 36),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(18, 18),
  };
};

// Function to get driver initials from name
const getDriverInitials = (name: string): string => {
  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2); // Return max 2 characters
};

// Function to create custom driver marker icon with driver initials and status color
const createDriverMarkerIcon = (status: string, driverName: string) => {
  const colors = getDriverStatusColor(status);
  const initials = getDriverInitials(driverName);

  // Create a canvas to render the icon
  const canvas = document.createElement('canvas');
  canvas.width = 40;
  canvas.height = 40;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    // Draw circle background with shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 2;
    ctx.fillStyle = colors.bg;
    ctx.beginPath();
    ctx.arc(20, 20, 18, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowColor = 'transparent';

    // Draw the driver initials
    ctx.fillStyle = colors.text;
    ctx.font =
      'bold 16px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, 20, 20);
  }

  return {
    url: canvas.toDataURL(),
    scaledSize: new google.maps.Size(40, 40),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(20, 20),
  };
};

// Helper function to get map center with fallback to company headquarters or default
const getMapCenter = (companyInfo: any): { lat: number; lng: number } => {
  // If company headquarters coordinates are available, use those
  if (
    companyInfo?.headquarters?.latitude &&
    companyInfo?.headquarters?.longitude
  ) {
    return {
      lat: companyInfo.headquarters.latitude,
      lng: companyInfo.headquarters.longitude,
    };
  }

  // Fallback to default center
  return DEFAULT_FALLBACK_CENTER;
};

// Helper function to filter drivers with valid location data
const getValidDrivers = (drivers: CompanyDriver[]): CompanyDriver[] => {
  const validDrivers = drivers.filter((driver) => {
    const hasValidLocation =
      driver.locationLatitude !== null &&
      driver.locationLatitude !== undefined &&
      driver.locationLatitude !== 0 &&
      driver.locationLongitude !== null &&
      driver.locationLongitude !== undefined &&
      driver.locationLongitude !== 0;

    return hasValidLocation;
  });

  return validDrivers;
};

const DriversScreen: React.FC<DriversScreenProps> = ({
  showHeader = true,
  activeTab = 'drivers',
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { driver: currentDriver } = useDriverStateV2();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBS,
  });

  const [callMarkers, setCallMarkers] = useState<
    Array<{ id: string; position: google.maps.LatLng; dispatch: any }>
  >([]);
  const [driverMarkers, setDriverMarkers] = useState<CompanyDriver[]>([]);
  const [shouldResetBounds, setShouldResetBounds] = useState(true);
  const [isMapReady, setIsMapReady] = useState(false);
  const [showCreateCall, setShowCreateCall] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const boundsInitializedRef = useRef(false); // Track if bounds have been set on initial load
  const mapRef = useRef<google.maps.Map | null>(null);

  // Memoize the options object to prevent unnecessary hook re-runs
  const driversQueryOptions = useMemo(
    () => ({ mapVisible: showMap, activeTab }),
    [showMap, activeTab]
  );
  const driversQuery = useCompanyDrivers(driversQueryOptions);
  const {
    data: driversData,
    isLoading: driversLoading,
    error: driversError,
    refetch: refetchDrivers,
    status,
  } = driversQuery;
  const drivers: CompanyDriver[] = (driversData as CompanyDriver[]) || [];

  // Sync driver data to state whenever it changes
  useEffect(() => {
    if (drivers && drivers.length > 0) {
      const validDrivers = getValidDrivers(drivers);
      setDriverMarkers(validDrivers);
    }
  }, [drivers]);

  // Add drivers as call markers and update their positions when they change
  // This runs after callMarkers have been populated with actual calls from geocoding
  useEffect(() => {
    if (
      driverMarkers &&
      driverMarkers.length > 0 &&
      !shouldResetBounds &&
      callMarkers.length > 0
    ) {
      // Only run after bounds have been set (which means call markers are populated)
      // Create driver markers with current positions
      const driverCallMarkers = driverMarkers.map((driver) => ({
        id: `driver-${driver.id}`,
        position: new google.maps.LatLng(
          driver.locationLatitude,
          driver.locationLongitude
        ),
        dispatch: {
          id: `driver-${driver.id}`,
          dispatchNumber: `Driver: ${driver.name}`,
          status: driver.status,
          pickupLocation: `${driver.name} location`,
        },
      }));

      // Get actual calls (non-driver markers)
      const actualCalls = callMarkers.filter(
        (m) => !m.id.startsWith('driver-')
      );

      // Always merge: combine actual calls with updated driver markers
      const merged = [...actualCalls, ...driverCallMarkers];
      setCallMarkers(merged);
    }
  }, [
    driverMarkers,
    shouldResetBounds,
    callMarkers.filter((m) => !m.id.startsWith('driver-')).length,
  ]);

  // Debug logging for driver data state
  useEffect(() => {
    // Intentionally empty - debug logging removed
  }, [
    drivers.length,
    driversLoading,
    driversError,
    driversData,
    status,
    driverMarkers.length,
    callMarkers.length,
  ]);

  const {
    data: dispatches = [],
    isLoading: dispatchesLoading,
    error: dispatchesError,
  } = useCompanyDispatches();

  // Log dispatches data whenever it changes
  useEffect(() => {
    const TAG = '🗺️ [DriversScreen-Dispatches]';
    console.log(`${TAG} Dispatches updated:`, {
      count: dispatches.length,
      isLoading: dispatchesLoading,
      error: dispatchesError?.message,
      firstDispatchCoords: dispatches[0]
        ? {
            id: dispatches[0].id,
            lat: dispatches[0].pickupLocationLat,
            lng: dispatches[0].pickupLocationLong,
          }
        : 'N/A',
      sample: dispatches.slice(0, 3).map((d) => ({
        id: d.id,
        dispatchNumber: d.dispatchNumber,
        status: d.status,
        lat: d.pickupLocationLat,
        lng: d.pickupLocationLong,
      })),
    });
  }, [dispatches, dispatchesLoading, dispatchesError]);
  const { data: customersData } = useGetCustomers();
  const { data: companyInfo } = useCompanyInfo();

  // Update TOWING call marker positions to match the assigned driver's location
  useEffect(() => {
    if (
      callMarkers.length > 0 &&
      driverMarkers.length > 0 &&
      dispatches.length > 0
    ) {
      const updatedCallMarkers = callMarkers.map((marker) => {
        // Only update if this is an actual call (not a driver marker) with TOWING status
        if (
          !marker.id.startsWith('driver-') &&
          marker.dispatch.status === 'TOWING'
        ) {
          // Find the top assigned driver for this dispatch
          const assignedDriver = dispatches.find(
            (d) => d.id === marker.dispatch.id
          )?.drivers?.[0];

          if (assignedDriver) {
            // Find the driver's current location from driverMarkers
            const driverLocation = driverMarkers.find(
              (dm) => dm.id === assignedDriver.id
            );

            if (
              driverLocation &&
              driverLocation.locationLatitude &&
              driverLocation.locationLongitude
            ) {
              return {
                ...marker,
                position: new google.maps.LatLng(
                  driverLocation.locationLatitude,
                  driverLocation.locationLongitude
                ),
              };
            }
          }
        }

        return marker;
      });

      // Only update state if something changed
      const hasChanges = updatedCallMarkers.some((m, i) => {
        const oldMarker = callMarkers[i];
        return (
          m.position.lat() !== oldMarker.position.lat() ||
          m.position.lng() !== oldMarker.position.lng()
        );
      });

      if (hasChanges) {
        setCallMarkers(updatedCallMarkers);
      }
    }
  }, [callMarkers, driverMarkers, dispatches]);

  // Reset bounds flag and invalidate driver query when component mounts or activeTab changes to drivers
  useEffect(() => {
    if (activeTab === 'drivers') {
      boundsInitializedRef.current = false; // Reset so bounds can be set on next load
      setShouldResetBounds(true);
      // Invalidate the query to ensure fresh data when coming back to drivers tab
      queryClient.invalidateQueries({ queryKey: ['company-drivers'] });
    }
  }, [activeTab, queryClient]);

  // Force refetch drivers when map becomes visible or when coming back to drivers tab
  useEffect(() => {
    if (showMap && activeTab === 'drivers') {
      if (driversQuery.isStale || !driversData) {
        refetchDrivers();
      }
    }
  }, [showMap, activeTab, driversQuery.isStale, driversData, refetchDrivers]);

  // Handle call click - navigate to the CallScreen
  const handleCallClick = (callId: string) => {
    navigate(`/calls/${callId}`);
  };

  // Handle add call button
  const handleAddCall = () => {
    setShowCreateCall(true);
  };

  // Fit map bounds to show all call locations when map loads (initial load only, not on refresh)
  // Uses original approach: geocode all locations, then fit bounds
  // This is simple and reliable. UI responsiveness fixed by updating JSX rendering logic
  useEffect(() => {
    // Only run on initial load, not on every refresh
    if (
      !isMapReady ||
      !shouldResetBounds ||
      !showMap ||
      boundsInitializedRef.current
    ) {
      return;
    }

    if (!mapRef.current) {
      return;
    }

    // Filter all non-COMPLETED dispatches (show all active/pending calls regardless of date)
    const activeDispatches = dispatches.filter((dispatch) => {
      return dispatch.status !== 'COMPLETED';
    });

    if (activeDispatches.length === 0) {
      return;
    }

    // Geocode all pickup locations and build bounds
    const geocoder = new google.maps.Geocoder();
    const bounds = new google.maps.LatLngBounds();
    let geocodedCount = 0;
    const newCallMarkers: Array<{
      id: string;
      position: google.maps.LatLng;
      dispatch: any;
    }> = [];
    let isMounted = true; // Track if component is still mounted

    activeDispatches.forEach((dispatch) => {
      if (!dispatch.pickupLocation) {
        return;
      }

      geocoder.geocode(
        { address: dispatch.pickupLocation },
        (results, status) => {
          // Only update state if component is still mounted
          if (!isMounted) {
            return;
          }

          if (
            status === google.maps.GeocoderStatus.OK &&
            results &&
            results[0]
          ) {
            const location = results[0].geometry.location;
            bounds.extend(location);
            newCallMarkers.push({
              id: dispatch.id,
              position: location,
              dispatch: dispatch,
            });
            geocodedCount++;

            // Once all addresses are geocoded, fit the bounds
            if (geocodedCount === activeDispatches.length) {
              // Double-check mapRef is still valid before using it
              if (mapRef.current && isMounted) {
                setCallMarkers(newCallMarkers);
                if (!bounds.isEmpty()) {
                  mapRef.current.fitBounds(bounds, {
                    top: 50,
                    right: 50,
                    bottom: 50,
                    left: 50,
                  });
                }
                // Mark as initialized AFTER geocoding completes successfully
                boundsInitializedRef.current = true;
                setShouldResetBounds(false);
              }
            }
          } else {
            geocodedCount++;

            // If geocoding fails for this address, continue with others
            if (geocodedCount === activeDispatches.length) {
              // Double-check mapRef is still valid before using it
              if (mapRef.current && isMounted) {
                setCallMarkers(newCallMarkers);
                if (!bounds.isEmpty()) {
                  mapRef.current.fitBounds(bounds, {
                    top: 50,
                    right: 50,
                    bottom: 50,
                    left: 50,
                  });
                }
                // Mark as initialized AFTER geocoding completes (even with failures)
                boundsInitializedRef.current = true;
                setShouldResetBounds(false);
              }
            }
          }
        }
      );
    });

    // Cleanup function: set isMounted to false when component unmounts
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapReady, shouldResetBounds, showMap]); // Intentionally exclude dispatches - effect should only run on initial load
  // Using dispatches inside but not in deps because we only want bounds set once, new dispatches just add/update markers

  // Effect to ensure markers are rendered when callMarkers are updated
  // NOTE: We do NOT update bounds here to prevent map from jumping on refresh
  // Bounds are only set once during initial load in the previous effect
  useEffect(() => {
    if (!mapRef.current || !isLoaded || callMarkers.length === 0 || !showMap)
      return;

    // The markers are rendered through the MapMarker component rendering loop
    // This effect just ensures the map is properly displayed when markers update
    // We intentionally do NOT call fitBounds here to avoid jumping on refresh
  }, [callMarkers, isLoaded, showMap]);

  // Trigger map resize when map becomes visible to ensure markers render properly
  useEffect(() => {
    if (showMap && mapRef.current && isMapReady) {
      // Trigger a small delay to let the DOM finish rendering
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
        // Also manually trigger map resize
        if (mapRef.current) {
          google.maps.event.trigger(mapRef.current, 'resize');
        }
      }, 100);
    }
  }, [showMap, isMapReady]);

  if (loadError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center text-red-600">
          <AlertCircle size={48} className="mx-auto mb-4" />
          <p>Error loading Google Maps</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  const TERMINAL_STATUSES = [
    'COMPLETED',
    'COMPLETED_AWAITING',
    'DECLINED',
    'CANCELED',
  ] as const;

  // Pass all dispatches from the 60-day window; sort pins "Me" to top and terminal calls to bottom
  const activeServiceCalls = dispatches.slice().sort((a, b) => {
    // Get current driver ID for "Me" check
    const currentDriverId = currentDriver?.id;

    // Check if calls are assigned to current driver
    const aIsMe = currentDriverId
      ? a.drivers?.some((d) => d.id === currentDriverId) || false
      : false;
    const bIsMe = currentDriverId
      ? b.drivers?.some((d) => d.id === currentDriverId) || false
      : false;

    // 1. "Me" calls always at top
    if (aIsMe && !bIsMe) return -1;
    if (!aIsMe && bIsMe) return 1;

    // 2. Terminal statuses (COMPLETED, COMPLETED_AWAITING, DECLINED) always at bottom
    const aIsTerminal = TERMINAL_STATUSES.includes(a.status as any);
    const bIsTerminal = TERMINAL_STATUSES.includes(b.status as any);

    if (aIsTerminal && !bIsTerminal) return 1;
    if (!aIsTerminal && bIsTerminal) return -1;

    // 3. PENDING status next to last
    const aIsPending = a.status === 'PENDING';
    const bIsPending = b.status === 'PENDING';

    if (aIsPending && !bIsPending) return 1;
    if (!aIsPending && bIsPending) return -1;

    // 4. All other sorting by createDate (newest first)
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  // Show CreateCallScreen if creating a new call
  if (showCreateCall) {
    return (
      <CreateCallScreen
        onBack={() => setShowCreateCall(false)}
        onSuccess={() => setShowCreateCall(false)}
        showHeader={showHeader}
      />
    );
  }

  return (
    <>
      {showHeader && (
        <Header
          driverName="Fleet Overview"
          status="ACTIVE"
          onMenuClick={() => {}}
          onStatusClick={() => {}}
          onStatusChange={() => {}}
          isEmbeddedMode={false}
        />
      )}

      <div className="flex-1 flex flex-col relative pb-36">
        {/* Floating Map Toggle Button */}
        <button
          onClick={() => setShowMap(!showMap)}
          className="absolute top-2 right-2 z-50 p-3 rounded-full bg-background transition-all duration-300 hover:scale-110"
          style={{
            boxShadow:
              '0 4px 12px rgba(0, 0, 0, 0.15), 0 8px 24px rgba(0, 0, 0, 0.1)',
          }}
          title={showMap ? 'Hide map' : 'Show map'}
        >
          {showMap ? (
            <Eye size={24} className="text-gray-700" />
          ) : (
            <Map size={24} className="text-gray-700" />
          )}
        </button>

        {/* Map Section - Never unmount GoogleMap, keep in DOM, control with CSS */}
        <div
          className="w-full overflow-hidden transition-all duration-500 ease-in-out"
          style={{
            maxHeight: showMap ? '400px' : '0px',
            opacity: showMap ? 1 : 0,
            visibility: showMap ? 'visible' : 'hidden',
            transition:
              'max-height 500ms ease-in-out, opacity 500ms ease-in-out, visibility 500ms ease-in-out',
          }}
        >
          <div className="w-full" style={{ height: '400px' }}>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              onLoad={(map) => {
                mapRef.current = map;
                setIsMapReady(true);
              }}
              center={
                boundsInitializedRef.current
                  ? undefined // Let fitBounds control the center after initial bounds are set
                  : getValidDrivers(drivers).length > 0
                    ? {
                        lat: getValidDrivers(drivers)[0].locationLatitude,
                        lng: getValidDrivers(drivers)[0].locationLongitude,
                      }
                    : getMapCenter(companyInfo)
              }
              zoom={12}
              options={{
                styles: [
                  {
                    elementType: 'geometry',
                    stylers: [{ color: '#f5f5f0' }],
                  },
                  {
                    elementType: 'labels.text.stroke',
                    stylers: [{ color: '#f5f5f0' }],
                  },
                  {
                    elementType: 'labels.text.fill',
                    stylers: [{ color: '#615856' }],
                  },
                  {
                    featureType: 'administrative',
                    elementType: 'geometry.stroke',
                    stylers: [{ color: '#d9d5d0' }],
                  },
                  {
                    featureType: 'administrative.land_parcel',
                    elementType: 'labels.text.fill',
                    stylers: [{ color: '#ae9e90' }],
                  },
                  {
                    featureType: 'landscape',
                    elementType: 'geometry',
                    stylers: [{ color: '#f5f5f0' }],
                  },
                  {
                    featureType: 'poi',
                    stylers: [{ visibility: 'off' }],
                  },
                  {
                    featureType: 'poi.business',
                    stylers: [{ visibility: 'off' }],
                  },
                  {
                    featureType: 'poi.park',
                    stylers: [{ visibility: 'off' }],
                  },
                  {
                    featureType: 'road',
                    elementType: 'geometry.fill',
                    stylers: [{ color: '#ffffff' }],
                  },
                  {
                    featureType: 'road.arterial',
                    elementType: 'geometry.fill',
                    stylers: [{ color: '#fad961' }],
                  },
                  {
                    featureType: 'road.highway',
                    elementType: 'geometry.fill',
                    stylers: [{ color: '#fad961' }],
                  },
                  {
                    featureType: 'road.highway',
                    elementType: 'geometry.stroke',
                    stylers: [{ color: '#e9bc3f' }],
                  },
                  {
                    featureType: 'road.local',
                    elementType: 'labels.text.fill',
                    stylers: [{ color: '#806033' }],
                  },
                  {
                    featureType: 'transit.line',
                    elementType: 'geometry',
                    stylers: [{ color: '#dfd5d6' }],
                  },
                  {
                    featureType: 'transit.station',
                    elementType: 'geometry',
                    stylers: [{ color: '#dfd5d6' }],
                  },
                  {
                    featureType: 'water',
                    elementType: 'geometry.fill',
                    stylers: [{ color: '#b5d6f7' }],
                  },
                  {
                    featureType: 'water',
                    elementType: 'labels.text.fill',
                    stylers: [{ color: '#520800' }],
                  },
                  {
                    featureType: 'water',
                    elementType: 'labels.text.stroke',
                    stylers: [{ color: '#f5f5f0' }],
                  },
                ],
                disableDefaultUI: true,
                zoomControl: false,
              }}
            >
              {/* Render all markers from callMarkers (includes both calls and drivers) */}
              {callMarkers.map((marker) => {
                const isDriver = marker.id.startsWith('driver-');

                if (isDriver) {
                  // Render driver marker with driver-specific styling
                  const driverData = driverMarkers.find(
                    (d) => `driver-${d.id}` === marker.id
                  );
                  if (!driverData) return null;

                  const icon = createDriverMarkerIcon(
                    driverData.status,
                    driverData.name
                  );

                  return (
                    <Marker
                      key={marker.id}
                      position={marker.position}
                      icon={icon}
                      title={`${driverData.name} (${driverData.truckNumber}) - ${driverData.status}`}
                      clickable={false}
                    />
                  );
                } else {
                  // Render call marker
                  return (
                    <Marker
                      key={marker.id}
                      position={marker.position}
                      icon={createCustomMarkerIcon(marker.dispatch.status)}
                      title={`Call #${marker.dispatch.dispatchNumber}`}
                      onClick={(e) => {
                        e.stop?.(); // Prevent event propagation
                        handleCallClick(marker.dispatch.id);
                      }}
                    />
                  );
                }
              })}
            </GoogleMap>
          </div>
        </div>

        {/* Dispatch List Section */}
        <div className="flex-1 px-4 py-4 overflow-y-auto">
          {(() => {
            // Show dispatches immediately once they load, even if drivers are still loading
            // Drivers are only used for the map, not for the dispatch list rendering
            if (dispatchesLoading) {
              console.log('🗺️ [DriversScreen-Render] Dispatches still loading');
              return (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={24} className="animate-spin text-primary" />
                  <span className="ml-2 text-gray-600">
                    Loading dispatch data...
                  </span>
                </div>
              );
            } else if (dispatchesError) {
              console.log(
                '🗺️ [DriversScreen-Render] Dispatches error:',
                dispatchesError
              );
              return (
                <div className="p-4 rounded-lg border border-red-200 bg-red-50">
                  <div className="flex items-center text-red-600">
                    <AlertCircle size={16} className="mr-2" />
                    <span className="text-sm">
                      Failed to load dispatch data
                    </span>
                  </div>
                </div>
              );
            } else {
              console.log(
                '🗺️ [DriversScreen-Render] Rendering dispatch list with ' +
                  activeServiceCalls.length +
                  ' calls'
              );
              return (
                <div className="space-y-6">
                  {/* Active Service Calls Section */}
                  <ActiveServiceCallsTable
                    dispatches={activeServiceCalls}
                    customers={customersData?.data}
                    showHeader={true}
                    onCallClick={handleCallClick}
                    showCanceledDispatches
                  />
                </div>
              );
            }
          })()}
        </div>
      </div>

      {/* Add Call Button - Fixed at bottom */}
      <CreateCallBar onClick={handleAddCall} label="Add Call" />
    </>
  );
};

export default DriversScreen;
